<script setup lang="ts">
import { Check, X } from "utruckin-icons";
import { IDropdown, IDropdownOptions } from "@/types/main";
import {
  computed,
  nextTick,
  onBeforeUnmount,
  onMounted,
  ref,
  watch,
} from "vue";

const props = withDefaults(
  defineProps<IDropdownOptions & { fullWidth?: boolean }>(),
  {
    position: "bottom",
    label: "",
    items: () => [],
    modelValue: "",
    disabled: false,
    fullWidth: false,
    showBadge: false,
    clearable: false,
    dropdownFullWidth: false,
    isAppendItem: false,
    creatable: false,
  }
);

const emit = defineEmits<{
  (e: "update:modelValue", v: number | string): void;
  (e: "search", v: string): void;
  (e: "enter"): void;
}>();

const isOpen = ref(false);
const rootRef = ref<HTMLElement | null>(null);
const inputRef = ref<HTMLInputElement | null>(null);
const search = ref("");
const highlightedIndex = ref(-1);
const itemRefs = ref<HTMLElement[]>([]);

const listStyle = ref({
  top: "0px",
  left: "0px",
  width: "200px",
  transformOrigin: "top",
});

const selectedItem = computed(() => {
  return props.items.find((i) => i.value === props.modelValue) ?? null;
});

const selectedLabel = computed(() => {
  if (selectedItem.value) {
    return selectedItem.value.name;
  }

  return typeof props.modelValue === "string" ? props.modelValue : "";
});
const selectedColor = computed(() => selectedItem.value?.color || null);

const hasSelection = computed(() => selectedLabel.value.length > 0);

// const filteredItems = computed(() => {
//   if (!search.value) return props.items;
//
//   return props.items.filter((item) =>
//     item.name.toLowerCase().includes(search.value.toLowerCase())
//   );
// });

const isNewValue = computed(() => {
  if (!props.creatable) return false;

  const val = search.value.trim().toLowerCase();
  if (!val) return false;

  return !props.items.some((item) => item.name.toLowerCase() === val);
});

const showCreateOption = computed(() => {
  return props.creatable && search.value.trim().length > 0 && isNewValue.value;
});

function updateListPosition() {
  if (!rootRef.value) return;
  const rect = rootRef.value.getBoundingClientRect();
  listStyle.value = {
    width: `${rect.width}px`,
    left: `${rect.left + window.scrollX}px`,
    top:
      props.position === "top"
        ? `${rect.top + window.scrollY - 6}px`
        : `${rect.bottom + window.scrollY + 6}px`,
    transformOrigin: props.position === "top" ? "bottom" : "top",
  };
}

const toggle = async (e?: any) => {
  if (e) e.stopPropagation();
  if (props.disabled) return;
  if (!isOpen.value) {
    search.value = "";
    emit("search", "");

    updateListPosition();
    isOpen.value = true;

    highlightedIndex.value = props.items.findIndex(
      (item) => item.value === props.modelValue
    );

    await nextTick();
    inputRef.value?.focus();

    return;
  }
  isOpen.value = false;
};

function onKeydown(e: KeyboardEvent) {
  switch (e.key) {
    case "ArrowDown":
      e.preventDefault();

      highlightedIndex.value = Math.min(
        highlightedIndex.value + 1,
        props.items.length - 1
      );

      scrollHighlightedIntoView();

      break;

    case "ArrowUp":
      e.preventDefault();

      highlightedIndex.value = Math.max(highlightedIndex.value - 1, 0);

      scrollHighlightedIntoView();

      break;

    case "Enter":
      e.preventDefault();

      if (highlightedIndex.value >= 0 && props.items[highlightedIndex.value]) {
        choose(props.items[highlightedIndex.value]);
        emit("enter");
        return;
      }

      if (props.creatable && isNewValue.value) {
        const newItem = {
          value: search.value.trim(),
          name: search.value.trim(),
          isNew: true,
        };

        choose(newItem as any);
        emit("enter");
      }

      break;
  }
}

function scrollHighlightedIntoView() {
  const el = itemRefs.value[highlightedIndex.value];

  if (el) {
    el.scrollIntoView({
      block: "nearest",
    });
  }
}

function choose(item: IDropdown) {
  emit("update:modelValue", item.value);
  isOpen.value = false;
  search.value = "";
  emit("search", "");
}

function onDocumentClick(e: MouseEvent) {
  const el = rootRef.value;
  if (!el) return;
  const target = e.target as Node | null;
  if (!target) return;
  if (!el.contains(target)) {
    if (isOpen.value) {
      isOpen.value = false;
      search.value = "";
    }
  }
}

const clearValue = () => {
  emit("update:modelValue", null);
};

onMounted(() => {
  search.value = "";
  document.addEventListener("click", onDocumentClick);
});

onBeforeUnmount(() => {
  document.removeEventListener("click", onDocumentClick);
});

watch(search, (val) => {
  emit("search", val);
});

defineExpose({
  focus: async () => {
    if (props.disabled) return;

    await nextTick();

    if (!isOpen.value) {
      await toggle();
    }

    await nextTick();

    inputRef.value?.focus?.();
  },
});
</script>

<template>
  <div
    ref="rootRef"
    class="c-cu-table-dropdown"
    :class="{
      active: isOpen,
      disabled: disabled,
      'full-width': fullWidth,
    }"
    :style="{ maxWidth: maxWidth }"
    @click="toggle"
  >
    <div class="value" :class="{ 'is-placeholder': !hasSelection }">
      <template v-if="isOpen">
        <input
          ref="inputRef"
          v-model="search"
          class="dropdown-input"
          type="text"
          :placeholder="label"
          @click.stop
          @keydown="onKeydown"
        />
      </template>

      <template v-else>
        <span
          v-if="hasSelection && selectedColor"
          class="color-dot"
          :style="{ backgroundColor: `var(${selectedColor})` }"
        />
        {{ selectedLabel || label }}
      </template>
    </div>

    <div class="actions">
      <div v-if="clearable && modelValue" class="clear" @click="clearValue">
        <X :size="20" />
      </div>
    </div>

    <Teleport to="body">
      <div
        v-if="isOpen"
        class="c-cu-table-dropdown__list"
        :style="{
          position: 'fixed',
          top: listStyle.top,
          left: listStyle.left,
          width: dropdownFullWidth ? 'undefined' : listStyle.width,
          transform: position === 'top' ? 'translateY(-100%)' : 'none',
        }"
      >
        <div
          v-if="showCreateOption"
          class="c-cu-table-dropdown__item create-new"
          @click.stop="
            choose({
              value: search,
              name: search,
              isNew: true,
            })
          "
        >
          Create "{{ search }}"
        </div>
        <div
          v-for="(item, index) in items"
          :key="item.value || index"
          :ref="(el: any) => { if (el) itemRefs[index] = el as HTMLElement }"
          class="c-cu-table-dropdown__item"
          :class="{ active: index === highlightedIndex }"
          @click.stop="choose(item)"
        >
          <div class="label">
            <span
              v-if="item.color"
              class="color-dot"
              :style="{ backgroundColor: `var(${item.color})` }"
            />
            <span class="item-text">{{ item.name }}</span>
            <Check v-if="item.value === modelValue" class="check" />
          </div>
          <div class="app" v-if="isAppendItem && item.app">
            {{ item.app.split(" ")[0] }}
          </div>
        </div>
      </div>
    </Teleport>
  </div>
</template>

<style scoped lang="scss">
.c-cu-table-dropdown {
  width: 100%;
  height: 44px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
  border-radius: 14px;
  padding: 10px 12px;
  cursor: pointer;
  position: relative;
  font-size: 14px;
  line-height: 20px;
  font-weight: 500;
  color: var(--color-label);
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;

  &:hover {
    border-color: var(--color-primary-disabled);
  }

  &.disabled {
    pointer-events: none;
    opacity: 0.6;
  }

  .value {
    overflow: hidden;
    white-space: nowrap;
    text-overflow: ellipsis;
    color: var(--color-label);
    background: transparent;
    display: flex;
    align-items: center;
    gap: 6px;

    &.is-placeholder {
      color: var(--color-label-secondary);
    }

    input {
      font-size: 14px;
      line-height: 20px;
      font-weight: 500;
      color: var(--color-label);
      border: none;
      outline: none;
      box-shadow: none;
      -webkit-appearance: none;
      appearance: none;
      background: transparent;

      &::placeholder {
        font-weight: 500;
      }

      &:focus,
      &:focus-visible,
      &:active {
        outline: none !important;
        border: none !important;
        box-shadow: none !important;
        -webkit-appearance: none;
      }
    }
  }

  .actions {
    display: flex;
    align-items: center;
    gap: 4px;

    .clear {
      display: flex;
      justify-content: center;
      align-items: center;
      width: 20px;
      height: 20px;
      color: var(--color-label-secondary);
      cursor: pointer;
    }

    .icon {
      display: flex;
      align-items: center;
      transition: 0.2s ease;
      background: transparent;

      &.rotate {
        transform: rotate(180deg);
      }
    }
  }
}
</style>

<style lang="scss">
.c-cu-table-dropdown__list {
  z-index: 99999;
  max-height: 280px;
  background: var(--color-system-background);
  border: 1px solid var(--color-border);
  border-radius: 14px;
  overflow: hidden;
  overflow-y: scroll;
  scrollbar-width: none;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.1);
  padding: 8px;
  background: var(--color-secondary-system-background);
}

.c-cu-table-dropdown__item {
  height: 32px;
  padding: 8px;
  color: var(--color-label);
  display: flex !important;
  justify-content: space-between;
  align-items: center;
  gap: 10px;
  cursor: pointer;
  font-size: 16px;
  font-weight: 500;
  flex-wrap: nowrap !important;
  border-radius: 8px;

  &.active {
    background: var(--color-system-background);
  }

  .label {
    display: flex;
    align-items: center;
    justify-content: space-between;
    width: 100%;
    flex-wrap: nowrap;
    min-width: 0;
    gap: 8px;
  }

  .app {
    font-size: 12px;
    line-height: 16px;
    font-weight: 500;
    color: var(--color-label-secondary);
  }

  //&:not(:last-child) {
  //  border-bottom: 1px solid var(--color-border);
  //}

  &:hover {
    background: var(--color-system-background);

    .item-text {
      background: transparent;
    }
  }

  .item-text {
    font-size: 14px;
    line-height: 20px;
    display: -webkit-box;
    -webkit-line-clamp: 2;
    -webkit-box-orient: vertical;
    overflow: hidden;
  }

  .color-dot {
    width: 16px;
    height: 16px;
    border-radius: 50%;
  }

  .check {
    width: 16px;
    color: var(--color-label-secondary);
    font-weight: 700;
  }
}
</style>
